import React from 'react';

/**
 * FeatureBoundary — låter en icke-kritisk funktion försvinna i stället för att
 * ta ner sidan med sig.
 *
 * Appens ErrorBoundary sitter runt `<Routes>` och visar ett felkort med
 * reload-knapp. Det är rätt för en trasig sida, men fel för ett tillägg:
 * `WalletChip` och notisklockan ligger i headern, alltså UTANFÖR den
 * boundaryn, och en renderkrasch där svartnar hela appen — inklusive kön och
 * vetot. Samma sak inifrån: tipswidgeten renderas i lobbyn, så en krasch i
 * poängekonomin hade ersatt lobbyn med ett felkort mitt i matchstarten.
 *
 * Därför är fallbacken här ingenting. Saldot, notisklockan eller tipsrutan
 * uteblir tyst (felet loggas i konsolen), och matchflödet fortsätter — det är
 * alltid rätt avvägning: man ska kunna spela match även när poängen strular.
 *
 * @param {Object} props - Komponentens props.
 * @param {string} [props.name] - Namn på funktionen, används i konsolloggen.
 * @param {React.ReactNode} [props.fallback] - Valfri ersättning; default inget.
 * @param {React.ReactNode} props.children - Funktionen som ska inneslutas.
 *
 * @example
 * <FeatureBoundary name="WalletChip">
 *   <WalletChip />
 * </FeatureBoundary>
 */
export default class FeatureBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error(
      `FeatureBoundary (${this.props.name || 'okänd funktion'}) fångade:`,
      error,
      info,
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
